import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import BlobField from '../components/BlobField';
import AppShell from '../components/AppShell';
import SocialsEditor from '../components/SocialsEditor';
import CoverUpload from '../components/CoverUpload';
import Toast from '../components/Toast';
import api, { formatApiErrorDetail } from '../api';
import { useAuth } from '../context/AuthContext';
import { sqrtPriceAt } from '../lib/curve';
import WizardTour from '../components/WizardTour';
import OnboardingChecklist from '../components/OnboardingChecklist';

/**
 * Phase 8 — Create-Clan Wizard (redesigned).
 *
 * Creator answers 3 human questions:
 *   1. How big is your active audience today?
 *   2. What would lifetime access be worth to a superfan?
 *   3. Vibe: Open / Focused / Exclusive?
 *
 * Live outputs (recomputed on every input change):
 *   - Max members
 *   - Primary sale earnings
 *   - Annual earnings range (10%-50% churn)
 *   - 5-year total range
 *
 * Behind the scenes the wizard maps to backend inputs
 * { audience_bucket, lifetime_value, vibe } — server derives multiplier,
 * supply_cap, min_bits_to_join, max_bits_per_user (see PHASE_8_SPEC.md).
 *
 * Step 2 is the classic perks + review + mint flow (mostly preserved).
 */

// ---- Constants — MUST stay in sync with /app/backend/routes/clans.py ----
// Opening-Bit-price picker: the wizard's Q1. Each pill shows the actual
// opening price (m × √1 = m for a sqrt curve) and maps 1:1 to a
// `bit_price_tier`. Round, relatable numbers so creators can eyeball
// "does a fan paying $X for the first Bit feel right for MY audience?".
// The `sub` line surfaces the follower-range this tier is calibrated for
// (see q1HelperLine2 for the exact rule-of-thumb copy).
const ENTRY_TICKETS = [
  { key: 'community', label: '0.10 pts', sub: 'Widest reach',   tier: 'community', is_default: false },
  { key: 'balanced',  label: '0.25 pts', sub: 'Balanced',       tier: 'balanced',  is_default: true  },
  { key: 'committed', label: '0.50 pts', sub: 'Committed',      tier: 'committed', is_default: false },
  { key: 'premium',   label: '1.00 pts', sub: 'Premium',        tier: 'premium',   is_default: false },
];

const PERK_OPTIONS = [
  { key: 'live-qa',       label: '🎙️ Weekly live Q&A',           color: 'orange' },
  { key: 'breakdowns',    label: '📊 Exclusive breakdowns',       color: 'pink' },
  { key: 'priority-dm',   label: '💬 Priority DM replies',        color: 'blue' },
  { key: 'early-access',  label: '⏱️ Early access to posts',      color: 'green' },
  { key: 'voice-chat',    label: '🎧 Members-only voice chats',   color: 'purple' },
  { key: 'raffle',        label: '🎁 Monthly holder raffle',      color: 'yellow' },
];

// Formatting helpers
const fmtInt = (n) => (Number.isFinite(n) ? Math.round(n).toLocaleString() : '—');
const fmtMoney = (n) => {
  if (!Number.isFinite(n)) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n)}`;
};
// Compact K/M with 1 decimal for large numbers, no decimal for <$1K. Used
// in milestone cards so mobile rows never wrap.
const fmtMoneyCompact = (n) => {
  if (!Number.isFinite(n)) return '$0';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 10_000)   return `$${(n / 1_000).toFixed(0)}K`;
  if (n >= 1_000)    return `$${(n / 1_000).toFixed(1)}K`;
  return `$${Math.round(n)}`;
};
// Range formatter for the "Total earnings" row: two compact numbers with
// an en-dash. Collapses to a single value if low == high (shouldn't happen
// in practice but defensive).
const fmtRange = (low, high) => {
  const l = fmtMoneyCompact(low);
  const h = fmtMoneyCompact(high);
  return l === h ? l : `${l}–${h}`;
};

// Milestone card — one per Bit-supply target (100, 1K, 10K, 50K). Renders
// 5 rows following the PDF layout: Bit price · Activity (≈20%) · Your
// royalty (5%) · One-time mint royalty · Total earnings (20-40% range).
// Emoji + color come from a per-milestone theme, stage payload is the
// output of `deriveTokenomics().stages.<key>`.
function MilestoneCard({ testid, emoji, emojiClass, title, holders, stage, t }) {
  return (
    <div className={`cc-milestone cc-milestone-${emojiClass}`} data-testid={testid}>
      <div className="cc-milestone-head">
        <span className={`cc-milestone-emoji cc-milestone-emoji-${emojiClass}`} aria-hidden="true">
          {emoji}
        </span>
        <div className="cc-milestone-titles">
          <span className="cc-milestone-title">{title}</span>
          <span className="cc-milestone-holders">{holders}</span>
        </div>
      </div>
      <div className="cc-milestone-rows">
        <div className="cc-milestone-row">
          <span className="cc-milestone-lbl">{t('createClan.rowBitPrice')}</span>
          <span className="cc-milestone-val">{fmtMoneyCompact(stage.price)}</span>
        </div>
        <div className="cc-milestone-row">
          <span className="cc-milestone-lbl">{t('createClan.rowActivity')}</span>
          <span className="cc-milestone-val">{fmtMoneyCompact(stage.activityLow)}</span>
        </div>
        <div className="cc-milestone-row">
          <span className="cc-milestone-lbl">{t('createClan.rowRoyalty')}</span>
          <span className="cc-milestone-val cc-milestone-val-blue">{fmtMoneyCompact(stage.royaltyLow)}</span>
        </div>
        <div className="cc-milestone-row">
          <span className="cc-milestone-lbl">{t('createClan.rowMintRoyalty')}</span>
          <span className="cc-milestone-val cc-milestone-val-amber">{fmtMoneyCompact(stage.mintBoost)}</span>
        </div>
        <div className="cc-milestone-row cc-milestone-row-total">
          <span className="cc-milestone-lbl">{t('createClan.rowTotalEarnings')}</span>
          <span className="cc-milestone-val cc-milestone-val-total">{fmtRange(stage.totalLow, stage.totalHigh)}</span>
        </div>
      </div>
    </div>
  );
}

// Curve V2 — sqrt bonding curve tiers. Mirrors `BIT_PRICE_TIERS` in
// backend/routes/clans.py. `m` is the multiplier — opening price = m × √1 = m.
const BIT_PRICE_TIERS = {
  community: 0.10,
  balanced:  0.25,
  committed: 0.50,
  premium:   1.00,
};

// Practical minimum entry per tier — the dollar budget at which any fan can
// join at any point on the curve.
const TIER_MIN_ENTRY = {
  community: 5,
  balanced:  25,
  committed: 50,
  premium:   100,
};

// Bits required to enter the Clan Feed, derived from vibe on sqrt clans.
// Mirrors backend `SQRT_MIN_BITS_BY_VIBE` in routes/clans.py.
const SQRT_MIN_BITS_BY_VIBE = {
  open:      1,
  focused:   5,
  exclusive: 10,
};

// Whale-pct cap for sqrt clans is UNIFORM 3% across all styles — no fan
// can ever own more than 5% of any Clan (with a floor of 100 Bits for
// early-launch participation, enforced by the backend). Not shown in the
// wizard UI anymore — silently enforced.
const SQRT_WHALE_PCT_BY_VIBE = {
  open:      0.05,
  focused:   0.05,
  exclusive: 0.05,
};

// Community-style picks now ONLY drive `vibe` (which controls
// `min_bits_to_join` — the feed-access floor). The bit-price multiplier
// is a separate decision the creator makes via ENTRY_TICKETS above.
// This decoupling lets a "big-tent, high-price" or a "tight-circle,
// cheap-entry" clan exist — creators shouldn't have to bundle price
// and access into one pick.
//
// The whale-pct cap remains a uniform 3% across all styles (see
// SQRT_WHALE_PCT_BY_VIBE above) — silently enforced, not shown here.
const COMMUNITY_STYLES = {
  big_tent: {
    key: 'big_tent',
    emoji: '🌱',
    name: 'Open House',
    tagline: 'Anyone can drop in',
    description: '1 Bit unlocks the feed · casual vibe · widest access',
    vibe: 'open',
  },
  real_fans: {
    key: 'real_fans',
    emoji: '🎯',
    name: 'Real Fans',
    tagline: 'Fans show up with skin in the game',
    description: '5 Bits to unlock the feed · engaged core community',
    vibe: 'focused',
    is_default: true,
  },
  inner_circle: {
    key: 'inner_circle',
    emoji: '💎',
    name: 'Inner Circle',
    tagline: 'Only your most committed fans get in',
    description: '10 Bits to unlock the feed · tight-knit inner room',
    vibe: 'exclusive',
  },
};

/**
 * Derive backend + display values from wizard inputs for sqrt Curve V2 clans.
 *
 * On sqrt curves supply is unlimited and there is no peak — the price at
 * bit N is `m × √N` and keeps rising forever, just slower. So we no
 * longer scale milestones to audience size; instead we show FOUR FIXED
 * universal milestones (100 / 1,000 / 10,000 / 50,000 Bits minted) so
 * creators of any size see numbers that motivate them — reaching the top
 * milestones is their job, but the wizard shows what the system is
 * capable of at each stage.
 *
 * At each milestone we also show a rough "≈ members" range. The formula
 * tightens at higher scales because established communities tend to have
 * a more concentrated holder base (a few whales holding much bigger bags)
 * — so the low/high spread narrows as Bits grow. Calibrated targets from
 * live creator conversations:
 *    • 100 Bits (focused):     4–20 members
 *    • 1,000 Bits (focused):   40–200 members
 *    • 10,000 Bits (focused):  800–1,500 members  (tightened from 400-2000)
 *    • 50,000 Bits (focused):  3,000–6,000 members
 * For other vibes we scale linearly by (5 / min_bits_to_join) so the
 * numbers stay coherent whether the creator picks Open House, Real Fans,
 * or Inner Circle.
 */
// Base member ranges at each Bit milestone, calibrated for the "focused"
// vibe (min_bits_to_join = 5). Other vibes scale linearly — see below.
const MEMBERS_BASE = {
  100:    { lo: 4,     hi: 20    },
  1000:   { lo: 40,    hi: 200   },
  10000:  { lo: 800,   hi: 1500  },
  50000:  { lo: 3000,  hi: 6000  },
};

function deriveTokenomics({ bitPriceTier, vibe }) {
  const m = BIT_PRICE_TIERS[bitPriceTier];
  if (!m || !vibe) return null;

  // Sqrt-family curve math — helpers live in /lib/curve.js so the
  // wizard preview stays in exact lock-step with TradingSheet, Trade,
  // Wallet, and the backend. CURVE_EXPONENT is the shared tuning knob.
  const priceAt = (n) => sqrtPriceAt(m, n);

  const minBitsToJoin = SQRT_MIN_BITS_BY_VIBE[vibe] || 1;
  const whalePct = SQRT_WHALE_PCT_BY_VIBE[vibe] || 0.05;
  const startingWhaleCap = 100;

  // Members estimate at a milestone. Base ranges are calibrated for focused
  // (min=5). Scale by (5 / min_bits_to_join) for other vibes.
  //   Open House  (min=1)  → scale 5x    (more members per Bit — 1-Bit floor)
  //   Real Fans   (min=5)  → scale 1x    (base)
  //   Inner Circle(min=10) → scale 0.5x  (fewer members — 10-Bit floor)
  const membersAt = (nBits) => {
    const base = MEMBERS_BASE[nBits];
    const scale = 5 / minBitsToJoin;
    if (!base) {
      // Fallback for arbitrary N (unused today, kept safe).
      const high = Math.max(1, Math.round(nBits / minBitsToJoin));
      const low  = Math.max(1, Math.round(nBits / (minBitsToJoin * 3)));
      return { low, high };
    }
    return {
      low:  Math.max(1, Math.round(base.lo * scale)),
      high: Math.max(1, Math.round(base.hi * scale)),
    };
  };

  // Creator earnings on sqrt V2: every buy AND every sell walks the curve
  // and pays the creator 5% of the trade's pts value. So the ONLY thing that
  // drives creator income is ongoing trading volume — there is no "primary
  // sale" phase (the old capped-supply linear curve had one; V2 does not).
  //
  // At supply N, we estimate annual trading volume as:
  //   ~20% turnover  ×  N Bits  ×  current price
  // i.e. long-term holders flip ~20% of their bag per year, which models a
  // real active fan community — a more motivating baseline than the earlier
  // ultra-conservative 15%. Real casinos comfortably exceed this. Kept
  // deliberately below crypto-market levels (~80%+) so the creator still
  // sees an honest number, not hype.
  const CREATOR_FEE = 0.05;
  // Two turnover assumptions: low = 20% (steady active clan), high = 40%
  // (very active clan with regular flipping). We show a RANGE on the
  // total-earnings line so creators see the plausible band, not a single
  // point estimate that reads as a promise.
  const ANNUAL_CHURN_LOW = 0.20;
  const ANNUAL_CHURN_HIGH = 0.40;
  const EXPONENT = 0.667;
  // Cumulative "mint boost" royalty — the ONE-SHOT total you earn as your
  // Clan grows from 0 → N Bits (fans minting along the curve for the first
  // time). Formula: ∫₀ᴺ multiplier × n^0.667 dn = m × N^1.667 / 1.667.
  // This is DIFFERENT from the annual passive royalty shown below — the
  // mint boost happens once, front-loaded, while the annual number keeps
  // paying every year at that supply level.
  const cumulativeMintValue = (nBits) =>
    m * Math.pow(nBits, EXPONENT + 1) / (EXPONENT + 1);
  const stage = (nBits) => {
    const price = priceAt(nBits);
    const volumeLow  = ANNUAL_CHURN_LOW  * nBits * price;
    const volumeHigh = ANNUAL_CHURN_HIGH * nBits * price;
    const royaltyLow  = volumeLow  * CREATOR_FEE;
    const royaltyHigh = volumeHigh * CREATOR_FEE;
    const mintBoost = cumulativeMintValue(nBits) * CREATOR_FEE;
    return {
      bits: nBits,
      price,
      // Kept for backwards compat with anything still reading these:
      tradingVolume: volumeLow,
      creatorTake: royaltyLow,
      // New range-based fields powering the milestone card:
      activityLow:  volumeLow,
      activityHigh: volumeHigh,
      royaltyLow,
      royaltyHigh,
      mintBoost,
      // Total earnings = one-time mint boost + first-year royalty (range)
      totalLow:  mintBoost + royaltyLow,
      totalHigh: mintBoost + royaltyHigh,
      members: membersAt(nBits),
    };
  };

  return {
    curveType: 'sqrt',
    multiplier: m,
    openingPrice: m,
    minBitsToJoin,
    startingWhaleCap,
    whalePct,
    tierMinEntry: TIER_MIN_ENTRY[bitPriceTier] || 10,
    // Universal price landmarks (audience-independent) — used by Step 3 review.
    landmarks: [
      { n: 1_000,  price: priceAt(1_000)  },
      { n: 10_000, price: priceAt(10_000) },
    ],
    // Fixed universal growth milestones (replaces audience-tied stages).
    stages: {
      early: stage(100),
      growing: stage(1_000),
      established: stage(10_000),
      mega: stage(50_000),
    },
  };
}

export default function CreateClan() {
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();
  const { t } = useTranslation();

  const [step, setStep] = useState(1);
  // Entry ticket = Q1 — creator picks a dollar-amount they'd feel fair as
  // an early-fan buy-in. Maps 1:1 to a `bit_price_tier` (curve multiplier).
  const [entryTicket, setEntryTicketRaw] = useState('balanced');
  const [bitPriceTier, setBitPriceTier] = useState('balanced');
  const setEntryTicket = (key) => {
    const t = ENTRY_TICKETS.find((x) => x.key === key);
    if (!t) return;
    setEntryTicketRaw(key);
    setBitPriceTier(t.tier);
  };
  // Community style = Q2 — controls ONLY `vibe` (feed access floor).
  // Decoupled from bit_price_tier so creators can mix any price × any style.
  const [communityStyle, setCommunityStyleRaw] = useState('real_fans');
  const [vibe, setVibe] = useState(COMMUNITY_STYLES.real_fans.vibe);
  const setCommunityStyle = (key) => {
    const style = COMMUNITY_STYLES[key];
    if (!style) return;
    setCommunityStyleRaw(key);
    setVibe(style.vibe);
  };
  const [perks, setPerks] = useState(['🎙️ Weekly live Q&A', '📊 Exclusive breakdowns']);
  // Earnings preview panel collapse state — defaults OPEN so first-time
  // creators see the numbers immediately. Toggling the header chevron
  // stashes their preference for the rest of this session so if they
  // collapse it, it stays collapsed while they tweak Bit-price tiers.
  const [earningsOpen, setEarningsOpen] = useState(true);
  const [customPerk, setCustomPerk] = useState('');
  const [existingClan, setExistingClan] = useState(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState('');
  const [mintFee, setMintFee] = useState(0);
  const [tourNonce, setTourNonce] = useState(0);
  // Phase 9.6 — Launch scheduling
  const [launchMode, setLaunchMode] = useState('now'); // 'now' | 'scheduled'
  const [launchAt, setLaunchAt] = useState(() => {
    // Default: 24 hours from now, rounded to the nearest hour, for local input
    const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
    d.setMinutes(0, 0, 0);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  // Creator's chosen IANA timezone — auto-detected on first render, but they
  // can override via a select with a curated shortlist + full IANA fallback.
  const [launchTz, setLaunchTz] = useState(() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; }
    catch { return 'UTC'; }
  });
  const [balance, setBalance] = useState(null);
  // Socials (step 2) — loaded on mount, editable via SocialsEditor
  const [socials, setSocials] = useState({});
  // Cover picture (step 2) — optional. Persisted via PATCH after mint succeeds.
  const [coverUrl, setCoverUrl] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get('/clans/mine').catch(() => ({ data: { clan: null } })).then((r) => {
      if (cancelled) return;
      setExistingClan(r.data.clan);
    });
    // Phase 9.1 — fetch admin-configurable mint fee + current balance so we
    // can render "(fixed) X pts" on Review and gate the Mint button.
    api.get('/settings/public').then(({ data }) => {
      if (!cancelled) setMintFee(Number(data.clan_mint_fee_pts || 0));
    }).catch(() => {});
    api.get('/wallet/balance').then(({ data }) => {
      if (!cancelled) setBalance(Number(data.balance || 0));
    }).catch(() => {});
    api.get('/socials').then(({ data }) => {
      if (!cancelled) setSocials(data.socials || {});
    }).catch(() => {});
    // Wizard Intent Sync — if the creator picked Launch Now / Schedule Launch
    // in the checklist above, pre-select the wizard's Step 3 toggle to match.
    // One-shot: only applied while `launchMode` is still at its initial default,
    // so a manual toggle later on the wizard is never overridden.
    api.get('/onboarding/checklist').then(({ data }) => {
      if (cancelled) return;
      const intent = data?.launch_intent;
      if (intent === 'now' || intent === 'scheduled') {
        setLaunchMode((prev) => prev === 'now' ? intent : prev);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const tk = useMemo(
    () => deriveTokenomics({ bitPriceTier, vibe }),
    [bitPriceTier, vibe],
  );

  const togglePerk = (label) => {
    setPerks((prev) => {
      if (prev.includes(label)) return prev.filter((x) => x !== label);
      if (prev.length >= 6) return prev;
      return [...prev, label];
    });
  };

  const mint = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        bit_price_tier: bitPriceTier,           // Curve V2 — server derives multiplier + sqrt curve
        vibe,
        perks,
        custom_perk: customPerk.trim() ? customPerk.trim().slice(0, 80) : null,
      };
      // Phase 9.6 — attach launch_at + launch_tz only when creator chose to schedule
      if (launchMode === 'scheduled' && launchAt) {
        payload.launch_at = new Date(launchAt).toISOString();
        payload.launch_tz = launchTz || 'UTC';
      }
      const { data } = await api.post('/clans', payload);
      // If the creator picked a cover picture in step 2, persist it now that
      // the Clan exists. Best-effort — a cover failure never blocks the mint.
      if (coverUrl) {
        try { await api.patch('/clans/mine', { cover_url: coverUrl }); }
        catch (_e) { /* silent — creator can retry from Manage Clan */ }
      }
      await refreshMe();
      setSuccess(data.clan);
    } catch (ex) {
      setError(formatApiErrorDetail(ex.response?.data?.detail) || 'Could not mint your Clan.');
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Blocked / success states ----
  if (existingClan === undefined) {
    return (
      <AppShell activeTab="clan-cta">
        <div className="loading-shell" data-testid="cc-loading">{t('createClan.loading')}</div>
      </AppShell>
    );
  }

  if (existingClan) {
    return (
      <AppShell activeTab="clan-cta">
        <div className="cc-shell">
          <OnboardingChecklist />
          <div className="cc-panel" data-testid="cc-already-has">
            <h1 className="cc-heading">{t('createClan.alreadyHasTitle')} <span>Clan</span></h1>
            <p className="cc-subcopy">{t('createClan.alreadyHasBody')}</p>
            <div className="cc-nav" style={{ justifyContent: 'center' }}>
              <button className="cc-btn-primary" onClick={() => navigate('/manage-clan')} data-testid="cc-go-manage">{t('createClan.goToManage')}</button>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  if (success) {
    return (
      <AppShell activeTab="clan-cta">
        <div className="cc-shell">
          <OnboardingChecklist />
          <div className="cc-panel" data-testid="cc-success">
            <h1 className="cc-heading">{t('createClan.successTitle')} <span>{t('createClan.successAccent')}</span> {t('createClan.successCelebrate')}</h1>
            <p className="cc-subcopy">
              <Trans
                i18nKey="createClan.successBody"
                values={{ handle: user?.handle }}
                components={{ 1: <b /> }}
              />
            </p>
            <div className="cc-nav" style={{ justifyContent: 'center' }}>
              <button className="cc-btn-primary" onClick={() => navigate(`/creator/${user?.handle}`)} data-testid="cc-view-profile">{t('createClan.viewMyClan')}</button>
            </div>
          </div>
        </div>
      </AppShell>
    );
  }

  // ---- Main wizard ----
  return (
    <AppShell activeTab="clan-cta">
      <div className="cc-shell">
        <OnboardingChecklist />
        <button
          className="back-btn"
          aria-label={t('createClan.backAria')}
          onClick={() => (step === 1 ? navigate('/') : setStep(step - 1))}
          data-testid="cc-back-btn"
        >
          <svg viewBox="0 0 24 24" fill="none" width="16" height="16">
            <path d="M15 4l-8 8 8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div className="cc-topbar">
          <div className="cc-stepper">
            <div className={`cc-seg${step > 1 ? ' done' : ' active'}`} data-testid="cc-seg-1" />
            <div className={`cc-seg${step === 2 ? ' active' : step > 2 ? ' done' : ''}`} data-testid="cc-seg-2" />
            <div className={`cc-seg${step === 3 ? ' active' : ''}`} data-testid="cc-seg-3" />
          </div>
          <span className="cc-step-label" data-testid="cc-step-label">{t('createClan.stepLabel', { current: step, total: 3 })}</span>
          <button
            type="button"
            className="cc-replay-tour"
            onClick={() => setTourNonce((n) => n + 1)}
            data-testid="cc-replay-tour-btn"
            title={t('createClan.replayTourTitle')}
          >
            {t('createClan.replayTour')}
          </button>
        </div>

        <WizardTour wizardStep={step} forceStart={tourNonce} />

        {step === 1 && (
          <div className="cc-panel" data-testid="cc-panel-1">
            <h1 className="cc-heading">{t('createClan.step1Title')} <span>{t('createClan.step1Accent')}</span></h1>
            <p className="cc-subcopy">{t('createClan.step1Subcopy')}</p>

            {/* First-time-creator callout — links out to the illustrated
                Creator Playbook. Also permanently pinned under Help
                Center so returning creators can find it later. */}
            <a
              href="/guides/creator-prep"
              target="_blank"
              rel="noopener noreferrer"
              className="cc-guide-callout"
              data-testid="cc-creator-guide-callout"
            >
              <span className="cc-guide-callout-emoji" aria-hidden="true">🚀</span>
              <span className="cc-guide-callout-body">
                <b>New here? Read the Creator Launch Playbook</b>
                <span className="cc-guide-callout-sub">6-step illustrated walkthrough — onboarding, referral link, priming your audience, launch day, compounding royalties. Also pinned under <b>Help Center</b>.</span>
              </span>
              <span className="cc-guide-callout-chev" aria-hidden="true">→</span>
            </a>

            {/* ─────────────────────────────────────────────────────────
                Container 1 — Bit price + live earnings preview

                Two clean questions in the wizard are now wrapped in two
                visually distinct glass panels. Container 1 holds the
                Bit-price picker AND the earnings preview so creators
                can tap different price tiers and see the milestone
                numbers shift IN THE SAME VIEWPORT — no scrolling
                required. The preview panel is collapsible (defaults
                open) via `earningsOpen` state.
            ───────────────────────────────────────────────────────── */}
            <div className="cc-container" data-testid="cc-container-price">
              {/* Q1 — Opening Bit price (drives sqrt-curve multiplier) */}
              <div className="cc-q-block">
              <div className="cc-q-label">{t('createClan.q1Label')}</div>
              <p className="cc-q-hint" data-testid="cc-ticket-hint">
                {t('createClan.q1Hint')}
              </p>
              <details className="cc-style-helper cc-helper-details" data-testid="cc-ticket-helper">
                <summary className="cc-style-helper-title">
                  <span>{t('createClan.notSure')}</span>
                  <span className="cc-helper-chevron" aria-hidden="true">▸</span>
                </summary>
                <div className="cc-style-helper-inner">
                  <p className="cc-style-helper-body">
                    <Trans i18nKey="createClan.q1HelperLine1" components={{ 1: <b /> }} />
                  </p>
                  <p className="cc-style-helper-body">
                    <Trans i18nKey="createClan.q1HelperLine2" components={{ 1: <b />, 2: <b />, 3: <b />, 4: <b />, 5: <b /> }} />
                  </p>
                  <p className="cc-style-helper-body">
                    <Trans i18nKey="createClan.q1HelperLine3" components={{ 1: <b /> }} />
                  </p>
                </div>
              </details>
              <div className="cc-action-cue">{t('createClan.pickOneBelow')}</div>
              <div className="cc-audience-grid" data-testid="cc-ticket-grid">
                {ENTRY_TICKETS.map((tick) => (
                  <button
                    key={tick.key}
                    type="button"
                    className={`cc-audience-btn cc-ticket-btn${entryTicket === tick.key ? ' active' : ''}${tick.is_default ? ' suggested' : ''}`}
                    onClick={() => setEntryTicket(tick.key)}
                    data-testid={`cc-ticket-${tick.key}`}
                    aria-pressed={entryTicket === tick.key}
                  >
                    <span className="cc-pick-dot" aria-hidden="true" />
                    {tick.is_default && <span className="cc-ticket-badge">{t('createClan.suggestedBadge')}</span>}
                    <div className="cc-ticket-amount">{tick.label}</div>
                    <div className="cc-ticket-sub">{t(`createClan.ticket${tick.sub.replace(' ', '')}`, tick.sub)}</div>
                  </button>
                ))}
              </div>
            </div>

                {tk && (
                  <div className="cc-earnings-panel" data-testid="cc-earnings-panel">
                    <button
                      type="button"
                      className="cc-earnings-header"
                      onClick={() => setEarningsOpen((v) => !v)}
                      aria-expanded={earningsOpen}
                      data-testid="cc-earnings-toggle"
                    >
                      <span className="cc-earnings-title">
                        <span className="cc-earnings-icon" aria-hidden="true">💰</span>
                        {t('createClan.previewTitle')}
                      </span>
                      <span className={`cc-earnings-chev${earningsOpen ? ' open' : ''}`} aria-hidden="true">▾</span>
                    </button>

                    {earningsOpen && (
                      <div className="cc-earnings-body" data-testid="cc-earnings-body">
                        <p className="cc-earnings-lede">
                          {t('createClan.previewLede')}
                        </p>

                        <div className="cc-milestones">
                          <MilestoneCard
                            testid="cc-scenario-early"
                            emoji="🌱"
                            emojiClass="green"
                            title={t('createClan.milestoneFirst100')}
                            holders={t('createClan.holders4to20')}
                            stage={tk.stages.early}
                            t={t}
                          />
                          <MilestoneCard
                            testid="cc-scenario-growing"
                            emoji="💎"
                            emojiClass="blue"
                            title={t('createClan.milestone1K')}
                            holders={t('createClan.holders40to200')}
                            stage={tk.stages.growing}
                            t={t}
                          />
                          <MilestoneCard
                            testid="cc-scenario-established"
                            emoji="🚀"
                            emojiClass="pink"
                            title={t('createClan.milestone10K')}
                            holders={t('createClan.holders800to1500')}
                            stage={tk.stages.established}
                            t={t}
                          />
                          <MilestoneCard
                            testid="cc-scenario-mega"
                            emoji="☀️"
                            emojiClass="amber"
                            title={t('createClan.milestone50K')}
                            holders={t('createClan.holders3000to6000')}
                            stage={tk.stages.mega}
                            t={t}
                          />
                        </div>

                        <div className="cc-earnings-footers">
                          <p className="cc-earnings-footer cc-earnings-footer-referral">
                            {t('createClan.previewFootnoteReferral')}
                          </p>
                          <p className="cc-earnings-footer cc-earnings-footer-perks">
                            {t('createClan.previewFine')}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
            </div>
            {/* ────── /Container 1 ────── */}

            {/* ─────────────────────────────────────────────────────────
                Container 2 — Community style + fan-cost consequence
                Isolated in its own glass panel so the "who gets in?"
                decision has its own dedicated visual space.
            ───────────────────────────────────────────────────────── */}
            <div className="cc-container" data-testid="cc-container-style">
              {/* Q2 — Community Style (controls feed-access floor only) */}
              <div className="cc-q-block">
              <div className="cc-q-label">{t('createClan.q2Label')}</div>
              <details className="cc-style-helper cc-helper-details" data-testid="cc-style-helper">
                <summary className="cc-style-helper-title">
                  <span>{t('createClan.notSure')}</span>
                  <span className="cc-helper-chevron" aria-hidden="true">▸</span>
                </summary>
                <div className="cc-style-helper-inner">
                  <p className="cc-style-helper-body">
                    <Trans i18nKey="createClan.q2HelperLine1" components={{ 1: <b /> }} />
                  </p>
                  <p className="cc-style-helper-body">
                    <Trans i18nKey="createClan.q2HelperLine2" components={{ 1: <b />, 2: <b />, 3: <b /> }} />
                  </p>
                </div>
              </details>
              <div className="cc-action-cue">{t('createClan.pickOneBelow')}</div>
              <div className="cc-style-grid" data-testid="cc-style-grid">
                {Object.values(COMMUNITY_STYLES).map((s) => {
                  const nameKey = s.key === 'big_tent' ? 'OpenHouse' : s.key === 'real_fans' ? 'RealFans' : 'InnerCircle';
                  return (
                    <button
                      key={s.key}
                      type="button"
                      onClick={() => setCommunityStyle(s.key)}
                      className={`cc-style-card${communityStyle === s.key ? ' active' : ''}${s.is_default ? ' suggested' : ''}`}
                      data-testid={`cc-style-${s.key}`}
                      aria-pressed={communityStyle === s.key}
                    >
                      <span className="cc-pick-dot" aria-hidden="true" />
                      {s.is_default && (
                        <span className="cc-style-badge" aria-hidden="true">{t('createClan.suggestedBadge')}</span>
                      )}
                      <div className="cc-style-head">
                        <span className="cc-style-emoji">{s.emoji}</span>
                        <span className="cc-style-name">{t(`createClan.style${nameKey}Name`)}</span>
                      </div>
                      <div className="cc-style-tagline">{t(`createClan.style${nameKey}Tagline`)}</div>
                      <div className="cc-style-desc">{t(`createClan.style${nameKey}Desc`)}</div>
                    </button>
                  );
                })}
              </div>
              {tk && (
                <div className="cc-vibe-consequence" data-testid="cc-vibe-consequence">
                  <Trans
                    i18nKey="createClan.vibeConsequence"
                    count={tk.minBitsToJoin}
                    values={{ count: tk.minBitsToJoin }}
                    components={{ 1: <b /> }}
                  />
                </div>
              )}
              </div>
            </div>
            {/* ────── /Container 2 ────── */}

            <div className="cc-nav">
              <button className="cc-btn-back" onClick={() => navigate('/settings')} data-testid="cc-cancel">{t('createClan.cancel')}</button>
              <button
                className="cc-btn-primary"
                onClick={() => setStep(2)}
                disabled={!tk}
                data-testid="cc-continue-btn"
              >
                {t('createClan.continueArrow')}
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="cc-panel" data-testid="cc-panel-2">
            <h1 className="cc-heading">{t('createClan.step2Title')} <span>{t('createClan.step2Accent')}</span></h1>
            <p className="cc-subcopy">
              {t('createClan.step2Subcopy')}
            </p>
            <SocialsEditor
              value={socials}
              onChange={setSocials}
              onError={(msg) => msg && setError(msg)}
            />

            <div style={{ marginTop: 20 }}>
              <h2 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 4px' }}>
                {t('createClan.coverPicture')}
              </h2>
              <p className="cc-subcopy" style={{ marginTop: 0, marginBottom: 10 }}>
                {t('createClan.coverPictureSub')}
              </p>
              <CoverUpload
                value={coverUrl}
                onChange={setCoverUrl}
                onError={(msg) => setError(msg)}
                disabled={submitting}
                testIdPrefix="cc-cover"
              />
            </div>

            <div className="cc-nav">
              <button className="cc-btn-back" onClick={() => setStep(1)} data-testid="cc-back-step-2">{t('createClan.back')}</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  className="cc-btn-back"
                  onClick={() => setStep(3)}
                  data-testid="cc-skip-socials"
                >{t('createClan.skipForNow')}</button>
                <button
                  className="cc-btn-primary"
                  onClick={() => setStep(3)}
                  data-testid="cc-continue-btn-2"
                >{t('createClan.continueArrow')}</button>
              </div>
            </div>
          </div>
        )}

        {step === 3 && tk && (
          <div className="cc-panel" data-testid="cc-panel-3">
            <h1 className="cc-heading">{t('createClan.step3Title')} <span>{t('createClan.step3Accent')}</span></h1>
            <p className="cc-subcopy">
              {t('createClan.step3Subcopy')}
            </p>

            <div className="cc-review">
              <div className="cc-review-row-id">
                <div
                  className="cc-review-avatar"
                  style={{ background: user?.gradient
                    ? `linear-gradient(135deg, ${user.gradient[0]}, ${user.gradient[1]})`
                    : 'linear-gradient(135deg, #FF5722, #FFC107)' }}
                >
                  {(user?.display_name || user?.handle || 'U')
                    .split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase()}
                </div>
                <div>
                  <div className="cc-review-name">{user?.display_name}{t('createClan.clanNameSuffix')}</div>
                  <div className="cc-review-handle">@{user?.handle}</div>
                </div>
              </div>

              <div className="cc-review-rows">
                <div className="cc-rr">
                  <span className="cc-rr-label">
                    {t('createClan.rrOpeningPrice')}
                    <span className="cc-rr-sub">{t('createClan.rrOpeningPriceSub')}</span>
                  </span>
                  <span className="cc-rr-value" data-testid="cc-review-ticket">
                    {ENTRY_TICKETS.find(x => x.key === entryTicket)?.label}
                    <span className="cc-rr-tech"> · {t(`createClan.ticket${ENTRY_TICKETS.find(x => x.key === entryTicket)?.sub?.replace(' ', '') || ''}`).toLowerCase()}</span>
                  </span>
                </div>
                <div className="cc-rr"><span className="cc-rr-label">{t('createClan.rrCommunityStyle')}</span>
                  <span className="cc-rr-value" data-testid="cc-review-style">
                    {COMMUNITY_STYLES[communityStyle]?.emoji} {t(`createClan.style${communityStyle === 'big_tent' ? 'OpenHouse' : communityStyle === 'real_fans' ? 'RealFans' : 'InnerCircle'}Name`)}
                    <span className="cc-rr-tech"> · {t(`createClan.style${communityStyle === 'big_tent' ? 'OpenHouse' : communityStyle === 'real_fans' ? 'RealFans' : 'InnerCircle'}Tagline`).toLowerCase()}</span>
                  </span>
                </div>
                <div className="cc-rr">
                  <span className="cc-rr-label">
                    {t('createClan.rrPriceAt1K')}
                    <span className="cc-rr-sub">{t('createClan.rrPriceAt1KSub')}</span>
                  </span>
                  <span className="cc-rr-value" data-testid="cc-review-price-1k">
                    {tk.landmarks[0].price.toFixed(2)} {t('common.pts')}
                  </span>
                </div>
                <div className="cc-rr">
                  <span className="cc-rr-label">
                    {t('createClan.rrPriceAt10K')}
                    <span className="cc-rr-sub">{t('createClan.rrPriceAt10KSub')}</span>
                  </span>
                  <span className="cc-rr-value" data-testid="cc-review-price-10k">
                    {tk.landmarks[1].price.toFixed(2)} {t('common.pts')}
                  </span>
                </div>
                <div className="cc-rr">
                  <span className="cc-rr-label">
                    {t('createClan.rrBitsToEnter')}
                    <span className="cc-rr-sub">{t('createClan.rrBitsToEnterSub')}</span>
                  </span>
                  <span className="cc-rr-value" data-testid="cc-review-min">
                    {tk.minBitsToJoin} {tk.minBitsToJoin === 1 ? t('common.bit') : t('common.bits')}
                  </span>
                </div>
                <div className="cc-rr"><span className="cc-rr-label">{t('createClan.rrMintFee')}</span>
                  <span className="cc-rr-value" data-testid="cc-review-mint-fee">{mintFee} {t('common.pts')}</span>
                </div>
              </div>
            </div>

            <div className="cc-perks">
              <div className="cc-perks-head">
                <span className="cc-perks-title">{t('createClan.perksHeadTitle')}</span>
                <span className="cc-perks-optional">{t('createClan.perksOptional')}</span>
              </div>
              <p className="cc-perks-sub">
                {t('createClan.perksSub')}
              </p>
              <div className="cc-perk-grid" data-testid="cc-perk-grid">
                {PERK_OPTIONS.map((p) => (
                  <div
                    key={p.key}
                    className={`cc-perk-chip cc-${p.color}${perks.includes(p.label) ? ' selected' : ''}`}
                    onClick={() => togglePerk(p.label)}
                    role="button"
                    tabIndex={0}
                    data-testid={`cc-perk-${p.key}`}
                  >
                    {p.label}
                  </div>
                ))}
              </div>
              <input
                type="text"
                className="cc-perk-input"
                placeholder={t('createClan.perksCustomPlaceholder')}
                value={customPerk}
                onChange={(e) => setCustomPerk(e.target.value.slice(0, 80))}
                data-testid="cc-custom-perk-input"
              />
            </div>

            <p className="cc-fine-print">
              {t('createClan.finePrint')}
            </p>

            {/* Phase 9.6 — Launch timing */}
            <div className="cc-launch-block" data-testid="cc-launch-block">
              <div className="cc-launch-head">{t('createClan.launchHead')}</div>
              <p className="cc-launch-sub">
                {t('createClan.launchSub')}
              </p>
              <div className="cc-launch-choice">
                <button
                  type="button"
                  className={`cc-launch-opt${launchMode === 'now' ? ' selected' : ''}`}
                  onClick={() => setLaunchMode('now')}
                  data-testid="cc-launch-now"
                >
                  <div className="cc-launch-opt-emoji">⚡</div>
                  <div className="cc-launch-opt-label">{t('createClan.launchNowLabel')}</div>
                  <div className="cc-launch-opt-hint">{t('createClan.launchNowHint')}</div>
                </button>
                <button
                  type="button"
                  className={`cc-launch-opt${launchMode === 'scheduled' ? ' selected' : ''}`}
                  onClick={() => setLaunchMode('scheduled')}
                  data-testid="cc-launch-scheduled"
                >
                  <div className="cc-launch-opt-emoji">📅</div>
                  <div className="cc-launch-opt-label">{t('createClan.launchScheduleLabel')}</div>
                  <div className="cc-launch-opt-hint">{t('createClan.launchScheduleHint')}</div>
                </button>
              </div>
              {launchMode === 'scheduled' && (
                <div className="cc-launch-schedule">
                  <label className="cc-launch-label" htmlFor="cc-launch-at-input">{t('createClan.launchDateLabel')}</label>
                  <div className="cc-launch-input-wrap">
                    <input
                      id="cc-launch-at-input"
                      type="datetime-local"
                      className="cc-launch-input"
                      value={launchAt}
                      onChange={(e) => setLaunchAt(e.target.value)}
                      data-testid="cc-launch-at-input"
                      min={(() => {
                        const d = new Date(Date.now() + 60 * 60 * 1000);
                        const pad = (n) => String(n).padStart(2, '0');
                        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                      })()}
                    />
                    <button
                      type="button"
                      className="cc-launch-ok"
                      onClick={() => {
                        const el = document.getElementById('cc-launch-at-input');
                        if (el && typeof el.blur === 'function') el.blur();
                        const cta = document.querySelector('[data-testid="cc-mint-btn"]');
                        if (cta && typeof cta.focus === 'function') cta.focus();
                      }}
                      disabled={!launchAt}
                      data-testid="cc-launch-ok"
                      title={t('createClan.launchOkTitle')}
                    >
                      {t('createClan.launchOk')}
                    </button>
                  </div>
                  <div className="cc-launch-fine">
                    {t('createClan.launchFine')}
                  </div>
                  {/* Timezone picker — creator picks their local TZ so
                      viewers see "Trading opens · <creator's local time>"
                      on the Trade + Launches pages. Auto-detected on
                      mount; searchable dropdown with a curated shortlist
                      + full IANA list below. */}
                  <div className="cc-launch-tz-wrap">
                    <label className="cc-launch-label" htmlFor="cc-launch-tz-select">Timezone</label>
                    <select
                      id="cc-launch-tz-select"
                      className="cc-launch-tz-select"
                      value={launchTz}
                      onChange={(e) => setLaunchTz(e.target.value)}
                      data-testid="cc-launch-tz-select"
                    >
                      <optgroup label="Common">
                        {[
                          'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
                          'America/Los_Angeles', 'America/Sao_Paulo', 'America/Mexico_City',
                          'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Madrid',
                          'Europe/Moscow', 'Africa/Cairo', 'Africa/Lagos', 'Africa/Johannesburg',
                          'Asia/Dubai', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Dhaka',
                          'Asia/Bangkok', 'Asia/Jakarta', 'Asia/Singapore', 'Asia/Hong_Kong',
                          'Asia/Shanghai', 'Asia/Tokyo', 'Asia/Seoul', 'Asia/Manila',
                          'Australia/Perth', 'Australia/Sydney', 'Australia/Melbourne',
                          'Pacific/Auckland', 'Pacific/Honolulu',
                        ].map((tz) => (
                          <option key={`c-${tz}`} value={tz}>{tz.replace(/_/g, ' ')}</option>
                        ))}
                      </optgroup>
                      <optgroup label="All timezones">
                        {(() => {
                          try {
                            const all = Intl.supportedValuesOf ? Intl.supportedValuesOf('timeZone') : [];
                            return all.map((tz) => (
                              <option key={`a-${tz}`} value={tz}>{tz.replace(/_/g, ' ')}</option>
                            ));
                          } catch { return null; }
                        })()}
                      </optgroup>
                    </select>
                    <div className="cc-launch-fine">
                      Detected: <b>{launchTz.replace(/_/g, ' ')}</b> — change if you want the countdown to display in a different time zone.
                    </div>
                  </div>
                </div>
              )}
            </div>

            {error && <p className="cc-err" data-testid="cc-mint-err">{error}</p>}

            <div className="cc-nav">
              <button className="cc-btn-back" onClick={() => setStep(2)} data-testid="cc-back-step-1">{t('createClan.back')}</button>
              <button
                className="cc-btn-primary"
                onClick={mint}
                disabled={submitting || (mintFee > 0 && balance !== null && balance < mintFee)}
                data-testid="cc-mint-btn"
              >
                {submitting
                  ? t('createClan.minting')
                  : (mintFee > 0 && balance !== null && balance < mintFee)
                    ? t('createClan.needPtsToMint', { amount: mintFee })
                    : launchMode === 'scheduled'
                      ? t('createClan.scheduleMintCta', { amount: mintFee })
                      : t('createClan.mintCta', { amount: mintFee })}
              </button>
            </div>
          </div>
        )}
      </div>
      {toast && <Toast msg={toast.msg} onDone={() => setToast(null)} />}
    </AppShell>
  );
}
